import { PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Input,
  Segmented,
  Select,
  Space,
  Typography,
  Table,
  Tag,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductsListProducts } from '@/api/generated/endpoints/products/products';
import type { ProductOut, ProductsListProductsStatus, Uom } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { ProductCreateModal } from '@/pages/catalog/ProductCreateModal';
import { ProductDrawer } from '@/pages/catalog/ProductDrawer';
import { uomLabel } from '@/lib/units';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';
import { statusOptions } from '@/pages/catalog/statusOptions';

export function ProductsPage() {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const table = useTableState<'q' | 'status' | 'uom'>({
    filters: ['q', 'status', 'uom'],
    defaultSort: 'name',
  });
  const canCreate = useHasRole('storekeeper');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [search, setSearch] = useState(table.filters.q[0] ?? '');
  const status = (table.filters.status[0] ?? 'active') as ProductsListProductsStatus;
  const uom = (table.filters.uom[0] ?? null) as Uom | null;
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const list = useProductsListProducts({
    ...table.query,
    q: table.filters.q[0] ?? null,
    status,
    uom,
  });

  // Телефон (T-177): товар — одной ячейкой: название, единица, срок годности, код.
  const mobileColumns: TableColumnsType<ProductOut> = [
    {
      key: 'name',
      title: t('products.colName'),
      dataIndex: 'name',
      sorter: true,
      sortOrder: table.sortOrderFor('name'),
      render: (name: string, p) => (
        <Space direction="vertical" size={2}>
          <Space size={4} wrap>
            <a>{name}</a>
            {!p.is_active && <Tag>{t('common.archived')}</Tag>}
          </Space>
          <Typography.Text type="secondary">
            {[
              uomLabel(t, p.base_uom),
              p.shelf_life_days ? t('common.days', { count: p.shelf_life_days }) : null,
              p.sku,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography.Text>
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<ProductOut> = [
    {
      key: 'name',
      title: t('products.colName'),
      dataIndex: 'name',
      sorter: true,
      sortOrder: table.sortOrderFor('name'),
      render: (name: string, p) => (
        <Space>
          <a>{name}</a>
          {!p.is_active && <Tag>{t('common.archived')}</Tag>}
        </Space>
      ),
    },
    {
      key: 'base_uom',
      title: t('products.colUom'),
      dataIndex: 'base_uom',
      width: 80,
      render: (u: Uom) => uomLabel(t, u),
    },
    {
      key: 'shelf_life_days',
      title: t('products.colShelfLife'),
      dataIndex: 'shelf_life_days',
      width: 140,
      render: (d: number | null) => (d ? t('common.days', { count: d }) : '—'),
    },
    {
      key: 'sku',
      title: t('products.colSku'),
      dataIndex: 'sku',
      width: 140,
      render: (s: string | null) => s ?? '—',
    },
    {
      key: 'updated_at',
      title: t('products.colUpdated'),
      dataIndex: 'updated_at',
      width: 140,
      sorter: true,
      sortOrder: table.sortOrderFor('updated_at'),
      render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
    },
  ];

  return (
    <Card
      title={t('products.title')}
      extra={
        canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCreating(true);
            }}
          >
            {t('products.add')}
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder={t('products.searchPlaceholder')}
          style={{ width: 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === '') table.setFilter('q', null);
          }}
          onSearch={(q) => {
            table.setFilter('q', q.trim() || null);
          }}
        />
        <Segmented
          options={statusOptions(t)}
          value={status}
          onChange={(v) => {
            table.setFilter('status', v === 'active' ? null : v);
          }}
        />
        <Select<Uom | 'all'>
          style={{ width: 120 }}
          value={uom ?? 'all'}
          options={[
            { label: t('uom.kgAndPcs'), value: 'all' },
            { label: t('uom.kg'), value: 'kg' },
            { label: t('uom.pcs'), value: 'pcs' },
            { label: t('uom.box'), value: 'box' },
          ]}
          onChange={(v) => {
            table.setFilter('uom', v === 'all' ? null : v);
          }}
        />
      </Space>
      <Table<ProductOut>
        rowKey="id"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isFetching}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        onRow={(p) => ({
          onClick: () => {
            setOpenId(p.id);
          },
          style: { cursor: 'pointer' },
        })}
      />
      <ProductCreateModal
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(p) => {
          setCreating(false);
          void message.success(t('products.created', { name: p.name }));
          setOpenId(p.id);
        }}
        queryClient={queryClient}
      />
      <ProductDrawer
        productId={openId}
        onClose={() => {
          setOpenId(null);
        }}
      />
    </Card>
  );
}
